// Recipe curation detail page. Two modes via the route:
//   /catalog/new — create a new recipe; on first save, redirects to
//                  /catalog/:id with the new id.
//   /catalog/:id — edit an existing recipe. Loads via GET /api/admin/
//                  recipes/:id, exposes metadata patch + ingredient
//                  replace.
//
// Edit model mirrors the menu-imports detail page:
//   - working: local shadow state initialized from the server response
//   - dirty: JSON.stringify(working) !== JSON.stringify(server)
//   - Save edits POSTs the whole working state in two parallel writes
//     (metadata + ingredients) so partial failures don't leave torn
//     state on a single field
//   - useBeforeUnload triggers the browser's native unsaved-changes
//     prompt
//
// Completeness warning: sum of ingredient grams vs declared yield. When
// it's <80% or >120% of recipe_yield_grams, render an amber banner.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AdminShell } from '@/components/AdminShell';
import { DishFamilyPicker } from '@/components/DishFamilyPicker';
import { IngredientPicker } from '@/components/IngredientPicker';
import {
  approveRecipe,
  createRecipe,
  deleteRecipe,
  getRecipe,
  patchRecipe,
  replaceRecipeIngredients,
  resolveRecipeAnomaly,
  type DishFamilySummary,
  type IngredientLookupMatch,
  type RecipeAnomaly,
  type RecipeConfidence,
  type RecipeDetail,
} from '@/api/admin';
import { ApiError } from '@/api/client';
import { formatRelativeTime } from '@/lib/format';

const CATEGORY_OPTIONS = [
  { value: '', label: '—' },
  { value: 'appetizer', label: 'Appetizer' },
  { value: 'main_course', label: 'Main course' },
  { value: 'side', label: 'Side' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'drink', label: 'Drink' },
] as const;

// Editable metadata that lives in the patch endpoint's surface.
interface MetadataState {
  variantName: string;
  description: string;
  recipeCategory: string;
  recipeYieldGrams: number;
  recipeYieldServings: number;
  sourceUrl: string;
  notes: string;
  confidence: RecipeConfidence;
}

interface IngredientRow {
  ingredientId: number;
  ingredientNameEn: string;
  ingredientNameRo: string;
  grams: number;
  role: string;
}

function metadataFromDetail(d: RecipeDetail): MetadataState {
  return {
    variantName: d.variantName,
    description: d.description ?? '',
    recipeCategory: d.recipeCategory ?? '',
    recipeYieldGrams: d.recipeYieldGrams,
    recipeYieldServings: d.recipeYieldServings,
    sourceUrl: d.sourceUrl ?? '',
    notes: d.notes ?? '',
    confidence: d.confidence,
  };
}

function ingredientsFromDetail(d: RecipeDetail): IngredientRow[] {
  return d.ingredients.map((i) => ({
    ingredientId: i.ingredientId,
    ingredientNameEn: i.ingredientNameEn,
    ingredientNameRo: i.ingredientNameRo,
    grams: i.grams,
    role: i.role ?? '',
  }));
}

export function CatalogDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const isCreate = idParam === 'new' || idParam === undefined;
  const id = !isCreate ? Number.parseInt(idParam!, 10) : NaN;

  if (!isCreate && (!Number.isFinite(id) || id <= 0)) {
    return (
      <AdminShell>
        <BackLink />
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">Not found</h1>
        <p className="mt-2 text-slate-600">
          That URL doesn't look like a valid recipe id.
        </p>
      </AdminShell>
    );
  }

  return isCreate ? <CreateForm /> : <EditForm id={id} />;
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

function CreateForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [family, setFamily] = useState<DishFamilySummary | null>(null);
  const [variantName, setVariantName] = useState('classic');
  const [yieldGrams, setYieldGrams] = useState<number>(400);
  const [category, setCategory] = useState<string>('');
  const [description, setDescription] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      if (!family) throw new Error('pick a dish family first');
      return createRecipe({
        dish_family_id: family.id,
        variant_name: variantName.trim(),
        recipe_yield_grams: yieldGrams,
        recipe_category: category || null,
        description: description.trim() || null,
        source_url: sourceUrl.trim() || null,
        ingredients: [],
      });
    },
    onSuccess: async ({ id }) => {
      setError(null);
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'recipes'],
        exact: false,
      });
      navigate(`/catalog/${id}`);
    },
    onError: (err: ApiError | Error) => {
      const msg = err instanceof Error ? err.message : String(err);
      // Friendly mapping for the two structured errors we know about.
      if (msg === 'duplicate_variant') {
        setError(
          'A recipe for that family + variant combination already exists. Pick a different variant name.',
        );
        return;
      }
      if (msg === 'invalid_reference') {
        setError(
          'One of the references is invalid (most likely the dish family). Pick again.',
        );
        return;
      }
      setError(msg);
    },
  });

  const canSubmit =
    family !== null && variantName.trim().length > 0 && yieldGrams > 0;

  return (
    <AdminShell>
      <BackLink />
      <h1 className="mt-3 text-2xl font-semibold tracking-tight">New recipe</h1>
      <p className="mt-1 text-sm text-slate-500">
        Pick a dish family and provide the basic metadata. Ingredients are
        added on the next screen. New recipes land as <strong>draft</strong>
        — they don't affect grading until you mark them verified.
      </p>

      <div className="mt-8 space-y-6 max-w-2xl">
        <Field label="Dish family" required>
          <DishFamilyPicker selected={family} onPick={setFamily} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
          <Field label="Variant name" required>
            <input
              type="text"
              className="input"
              placeholder="e.g. classic, cu mac, fără gluten"
              value={variantName}
              onChange={(e) => setVariantName(e.target.value)}
            />
          </Field>
          <Field
            label="Total yield (g)"
            required
            hint="Total cooked mass for the whole recipe"
          >
            <input
              type="number"
              className="input tabular-nums"
              min={1}
              max={50000}
              value={yieldGrams}
              onChange={(e) => setYieldGrams(Number(e.target.value))}
            />
          </Field>
        </div>

        <Field label="Category">
          <select
            className="select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Description">
          <textarea
            className="input min-h-[4rem] py-2"
            placeholder="Optional. Brief description of the dish."
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 4000))}
          />
        </Field>

        <Field label="Source URL">
          <input
            type="url"
            className="input"
            placeholder="Optional. Where the recipe came from."
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
          />
        </Field>

        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <Link to="/catalog" className="btn-ghost text-sm">
            Cancel
          </Link>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => create.mutate()}
            disabled={!canSubmit || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create recipe'}
          </button>
        </div>
      </div>
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

function EditForm({ id }: { id: number }) {
  const detail = useQuery<RecipeDetail, ApiError>({
    queryKey: ['admin', 'recipes', id],
    queryFn: ({ signal }) => getRecipe(id, signal),
    retry: false,
  });

  if (detail.isLoading) {
    return (
      <AdminShell>
        <p className="text-slate-500">Loading recipe…</p>
      </AdminShell>
    );
  }

  if (detail.isError) {
    if (detail.error.status === 404) {
      return (
        <AdminShell>
          <BackLink />
          <h1 className="mt-3 text-2xl font-semibold tracking-tight">Not found</h1>
          <p className="mt-2 text-slate-600">No recipe with id {id}.</p>
        </AdminShell>
      );
    }
    return (
      <AdminShell>
        <BackLink />
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          Something went wrong
        </h1>
        <p className="mt-2 text-slate-600">
          <code className="font-mono text-sm">{detail.error.message}</code>
        </p>
        <button
          type="button"
          className="btn-outline mt-4"
          onClick={() => void detail.refetch()}
        >
          Retry
        </button>
      </AdminShell>
    );
  }

  return (
    <AdminShell>
      <EditFormContent detail={detail.data!} query={detail} />
    </AdminShell>
  );
}

function EditFormContent({
  detail,
  query,
}: {
  detail: RecipeDetail;
  query: UseQueryResult<RecipeDetail, ApiError>;
}) {
  const queryClient = useQueryClient();
  const [metadata, setMetadata] = useState<MetadataState>(() =>
    metadataFromDetail(detail),
  );
  const [ingredients, setIngredients] = useState<IngredientRow[]>(() =>
    ingredientsFromDetail(detail),
  );
  const [error, setError] = useState<string | null>(null);

  // Re-sync working state when the server-side detail changes (post-save
  // refetch). Same pattern as DraftDetailPage.
  const lastSyncedRef = useRef<string>(
    JSON.stringify({ m: metadataFromDetail(detail), i: ingredientsFromDetail(detail) }),
  );
  useEffect(() => {
    const incoming = JSON.stringify({
      m: metadataFromDetail(detail),
      i: ingredientsFromDetail(detail),
    });
    if (incoming !== lastSyncedRef.current) {
      setMetadata(metadataFromDetail(detail));
      setIngredients(ingredientsFromDetail(detail));
      lastSyncedRef.current = incoming;
    }
  }, [detail]);

  const metadataDirty = useMemo(
    () => JSON.stringify(metadata) !== JSON.stringify(metadataFromDetail(detail)),
    [metadata, detail],
  );
  const ingredientsDirty = useMemo(
    () =>
      JSON.stringify(ingredients) !== JSON.stringify(ingredientsFromDetail(detail)),
    [ingredients, detail],
  );
  const isDirty = metadataDirty || ingredientsDirty;

  // Browser-native unsaved-changes prompt on tab close / nav-away.
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const save = useMutation({
    mutationFn: async () => {
      // Two parallel writes: metadata (PATCH) + ingredients (POST). If
      // either fails we surface the error and the other one may have
      // succeeded — that's acceptable because the user can hit Save
      // again and the still-dirty side will re-attempt.
      const tasks: Array<Promise<unknown>> = [];
      if (metadataDirty) {
        tasks.push(
          patchRecipe(detail.id, {
            variant_name: metadata.variantName,
            description: metadata.description || null,
            recipe_category: metadata.recipeCategory || null,
            recipe_yield_grams: metadata.recipeYieldGrams,
            recipe_yield_servings: metadata.recipeYieldServings,
            source_url: metadata.sourceUrl || null,
            notes: metadata.notes || null,
            confidence: metadata.confidence,
          }),
        );
      }
      if (ingredientsDirty) {
        tasks.push(
          replaceRecipeIngredients(detail.id, {
            ingredients: ingredients.map((i) => ({
              ingredient_id: i.ingredientId,
              grams: i.grams,
              role: i.role || null,
            })),
          }),
        );
      }
      await Promise.all(tasks);
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'recipes'],
        exact: false,
      });
    },
    onError: (err: ApiError | Error) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const navigate = useNavigate();
  const del = useMutation({
    mutationFn: () => deleteRecipe(detail.id),
    onSuccess: async ({ family_deleted }) => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'recipes'],
        exact: false,
      });
      // Back to the list — this detail page's recipe is gone.
      navigate('/catalog', {
        replace: true,
        state: { deletedFamily: family_deleted },
      });
    },
    onError: (err: ApiError | Error) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  function confirmDelete() {
    const msg =
      `Delete "${detail.dishFamilyNameEn} · ${detail.variantName}"?\n\n` +
      'If this is the family\'s last variant, the dish family and its ' +
      'aliases are removed too. Menu items that matched it become ' +
      'unmatched and re-resolve on the next regrade. This cannot be undone.';
    if (window.confirm(msg)) del.mutate();
  }

  function revert() {
    setMetadata(metadataFromDetail(detail));
    setIngredients(ingredientsFromDetail(detail));
    setError(null);
  }

  function addIngredient(match: IngredientLookupMatch) {
    // No duplicate canonical ids — the backend would reject + the user
    // can't grade the same ingredient twice anyway.
    if (ingredients.some((i) => i.ingredientId === match.ingredient_id)) return;
    setIngredients([
      ...ingredients,
      {
        ingredientId: match.ingredient_id,
        ingredientNameEn: match.ingredient_name_en,
        ingredientNameRo: match.ingredient_name_ro,
        grams: 50,
        role: '',
      },
    ]);
  }

  function updateIngredient(index: number, patch: Partial<IngredientRow>) {
    setIngredients(
      ingredients.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  }

  function deleteIngredient(index: number) {
    setIngredients(ingredients.filter((_, i) => i !== index));
  }

  // Completeness check: sum of ingredient grams vs declared yield.
  // Warn outside the 0.8..1.2 band.
  const sumGrams = ingredients.reduce((s, r) => s + r.grams, 0);
  const yieldRatio = metadata.recipeYieldGrams > 0
    ? sumGrams / metadata.recipeYieldGrams
    : 0;
  const completenessWarning =
    ingredients.length > 0 && (yieldRatio < 0.8 || yieldRatio > 1.2);

  return (
    <div>
      <BackLink />

      <header className="mt-3 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {detail.dishFamilyNameEn}
            <span className="text-slate-400 font-normal"> · {metadata.variantName}</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Updated {formatRelativeTime(detail.updatedAt)}
            {detail.dishFamilyNameEn !== detail.dishFamilyNameRo && (
              <>
                <span className="mx-2">·</span>
                <span className="text-slate-600">{detail.dishFamilyNameRo}</span>
              </>
            )}
          </p>
        </div>
        <ConfidenceToggle
          confidence={metadata.confidence}
          onChange={(c) => setMetadata({ ...metadata, confidence: c })}
        />
      </header>

      <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <span className="font-medium text-slate-900">Family attributes:</span>{' '}
        {detail.familyAttributes.proteins.length > 0
          ? detail.familyAttributes.proteins
            .map((protein) => `${protein.code} ${Math.round(protein.confidence * 100)}%`)
            .join(' + ')
          : 'no protein evidence'}
        {' · '}{detail.familyAttributes.cookingMethod ?? 'unknown method'}
        {detail.familyAttributes.methodConfidence !== null
          ? ` ${Math.round(detail.familyAttributes.methodConfidence * 100)}%`
          : ''}
        {' · '}{detail.familyAttributes.baseForm ?? 'unknown form'}
        {' · '}{detail.familyAttributes.dishConcept ?? 'unknown concept'}
        <span className="ml-1 text-xs text-slate-400">
          (v{detail.familyAttributes.attrVersion ?? '—'} · {detail.familyAttributes.attrModel ?? 'unattributed'})
        </span>
      </div>

      <EditToolbar
        isDirty={isDirty}
        isSaving={save.isPending}
        error={error}
        onSave={() => save.mutate()}
        onRevert={revert}
        onRefresh={() => void query.refetch()}
        onDelete={confirmDelete}
        isDeleting={del.isPending}
      />

      <ProvenancePanel detail={detail} onChanged={() => void query.refetch()} />

      {completenessWarning && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1">
          <div>
            Ingredients sum to <strong>{sumGrams} g</strong> but yield is{' '}
            <strong>{metadata.recipeYieldGrams} g</strong> (
            {Math.round(yieldRatio * 100)}%).
          </div>
          <div className="text-xs text-amber-800">
            Yield should be the total cooked mass — the amount of food the
            recipe produces. If the ingredients are right, set yield to match
            their sum. If the recipe serves more than one person, bump{' '}
            <strong>Servings</strong> too.
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-8 lg:grid-cols-[2fr_3fr]">
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Metadata
          </h2>
          <MetadataForm metadata={metadata} onChange={setMetadata} />
        </section>
        <section>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Ingredients
            <span className="ml-2 font-normal text-slate-400">
              {ingredients.length} · {sumGrams} g total
            </span>
          </h2>
          <IngredientsEditor
            ingredients={ingredients}
            onAdd={addIngredient}
            onUpdate={updateIngredient}
            onDelete={deleteIngredient}
          />
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetadataForm({
  metadata,
  onChange,
}: {
  metadata: MetadataState;
  onChange: (m: MetadataState) => void;
}) {
  function patch(p: Partial<MetadataState>) {
    onChange({ ...metadata, ...p });
  }
  return (
    <div className="space-y-4">
      <Field label="Variant name">
        <input
          type="text"
          className="input"
          value={metadata.variantName}
          onChange={(e) => patch({ variantName: e.target.value })}
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Total yield (g)" hint="Total cooked mass — should match the ingredient sum">
          <input
            type="number"
            className="input tabular-nums"
            min={1}
            value={metadata.recipeYieldGrams}
            onChange={(e) => patch({ recipeYieldGrams: Number(e.target.value) })}
          />
        </Field>
        <Field label="Servings" hint="How many people this recipe feeds">
          <input
            type="number"
            className="input tabular-nums"
            min={1}
            value={metadata.recipeYieldServings}
            onChange={(e) => patch({ recipeYieldServings: Number(e.target.value) })}
          />
        </Field>
      </div>
      <Field label="Category">
        <select
          className="select"
          value={metadata.recipeCategory}
          onChange={(e) => patch({ recipeCategory: e.target.value })}
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Description">
        <textarea
          className="input min-h-[5rem] py-2"
          value={metadata.description}
          onChange={(e) => patch({ description: e.target.value.slice(0, 4000) })}
        />
      </Field>
      <Field label="Source URL">
        <input
          type="url"
          className="input"
          value={metadata.sourceUrl}
          onChange={(e) => patch({ sourceUrl: e.target.value })}
        />
      </Field>
      <Field label="Notes">
        <textarea
          className="input min-h-[3rem] py-2"
          value={metadata.notes}
          onChange={(e) => patch({ notes: e.target.value.slice(0, 4000) })}
        />
      </Field>
    </div>
  );
}

function IngredientsEditor({
  ingredients,
  onAdd,
  onUpdate,
  onDelete,
}: {
  ingredients: IngredientRow[];
  onAdd: (match: IngredientLookupMatch) => void;
  onUpdate: (index: number, patch: Partial<IngredientRow>) => void;
  onDelete: (index: number) => void;
}) {
  return (
    <div>
      <div className="mb-3">
        <IngredientPicker
          onPick={onAdd}
          placeholder="Add ingredient — search by name (EN or RO)…"
        />
      </div>

      {ingredients.length === 0 ? (
        <div className="card p-6 text-sm text-slate-500 text-center">
          No ingredients yet. Use the search box above to add some.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider">
                  Ingredient
                </th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-right">
                  Grams
                </th>
                <th className="px-3 py-2 text-xs font-medium uppercase tracking-wider">
                  Role
                </th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ingredients.map((row, i) => (
                <tr key={`${row.ingredientId}-${i}`}>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-slate-900">
                      {row.ingredientNameEn}
                    </div>
                    {row.ingredientNameEn !== row.ingredientNameRo && (
                      <div className="text-xs text-slate-500">
                        {row.ingredientNameRo}
                      </div>
                    )}
                    <div className="text-xs text-slate-400">
                      id {row.ingredientId}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top w-24">
                    <input
                      type="number"
                      className="input tabular-nums w-full"
                      min={0}
                      value={row.grams}
                      onChange={(e) =>
                        onUpdate(i, { grams: Number(e.target.value) })
                      }
                    />
                  </td>
                  <td className="px-3 py-2 align-top w-40">
                    <input
                      type="text"
                      className="input text-xs"
                      placeholder="e.g. main, sauce"
                      value={row.role}
                      onChange={(e) => onUpdate(i, { role: e.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2 align-top w-12 text-right">
                    <button
                      type="button"
                      className="btn-ghost text-xs text-rose-700 hover:bg-rose-50"
                      onClick={() => onDelete(i)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConfidenceToggle({
  confidence,
  onChange,
}: {
  confidence: RecipeConfidence;
  onChange: (c: RecipeConfidence) => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white p-0.5 text-xs">
      <button
        type="button"
        className={
          'rounded-full px-3 py-1 transition-colors ' +
          (confidence === 'draft'
            ? 'bg-amber-100 text-amber-900'
            : 'text-slate-500 hover:bg-slate-50')
        }
        onClick={() => onChange('draft')}
      >
        Draft
      </button>
      <button
        type="button"
        className={
          'rounded-full px-3 py-1 transition-colors ' +
          (confidence === 'verified'
            ? 'bg-emerald-100 text-emerald-900'
            : 'text-slate-500 hover:bg-slate-50')
        }
        onClick={() => onChange('verified')}
      >
        Verified
      </button>
    </div>
  );
}

function EditToolbar({
  isDirty,
  isSaving,
  error,
  onSave,
  onRevert,
  onRefresh,
  onDelete,
  isDeleting,
}: {
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  onSave: () => void;
  onRevert: () => void;
  onRefresh: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="mt-4 card p-3 flex items-center gap-3 text-sm">
      <span
        className={
          'inline-flex items-center gap-1.5 ' +
          (isDirty ? 'text-amber-700' : 'text-slate-500')
        }
      >
        <span
          className={
            'h-2 w-2 rounded-full ' +
            (isDirty ? 'bg-amber-500' : 'bg-slate-300')
          }
        />
        {isDirty ? 'Unsaved changes' : 'No changes'}
      </span>
      <div className="flex-1" />
      {error && (
        <span className="text-xs text-rose-900" title={error}>
          Save failed: {error}
        </span>
      )}
      <button
        type="button"
        className="btn-ghost text-xs text-rose-700 hover:text-rose-900"
        onClick={onDelete}
        disabled={isSaving || isDeleting}
        title="Delete this recipe. If it's the family's last variant, the dish family and its aliases are removed too."
      >
        {isDeleting ? 'Deleting…' : 'Delete'}
      </button>
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={onRefresh}
        disabled={isSaving}
      >
        Refresh
      </button>
      <button
        type="button"
        className="btn-ghost text-xs"
        onClick={onRevert}
        disabled={!isDirty || isSaving}
      >
        Revert
      </button>
      <button
        type="button"
        className="btn-primary text-xs"
        onClick={onSave}
        disabled={!isDirty || isSaving}
      >
        {isSaving ? 'Saving…' : 'Save edits'}
      </button>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
        {required && <span className="ml-1 text-rose-700">*</span>}
      </span>
      {children}
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

function BackLink() {
  return (
    <Link
      to="/catalog"
      className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-2"
    >
      ← Catalog
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Provenance + anomaly panel (CATALOGUE_QUALITY_PIPELINE Phase 4)
// ---------------------------------------------------------------------------

const SEVERITY_CLASSES: Record<RecipeAnomaly['severity'], string> = {
  block: 'bg-rose-100 text-rose-900 ring-rose-200',
  warn: 'bg-amber-100 text-amber-900 ring-amber-200',
  info: 'bg-slate-100 text-slate-700 ring-slate-200',
};

/** Where this recipe came from + what the agents found. Sits between the
 *  toolbar and the edit form so the founder sees the trust context before
 *  editing. Hidden entirely for recipes with founder provenance and no
 *  findings (the common pre-pipeline case — zero visual noise). */
function ProvenancePanel({
  detail,
  onChanged,
}: {
  detail: RecipeDetail;
  onChanged: () => void;
}) {
  const open = detail.anomalies.filter((a) => a.resolvedAt === null);
  const resolvedCount = detail.anomalies.length - open.length;
  const [busyId, setBusyId] = useState<number | null>(null);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showProvenance = detail.source !== 'founder' || detail.anomalies.length > 0;
  if (!showProvenance) return null;

  async function approve() {
    setApproving(true);
    setError(null);
    try {
      await approveRecipe(detail.id);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApproving(false);
    }
  }

  async function resolve(anomalyId: number) {
    setBusyId(anomalyId);
    setError(null);
    try {
      await resolveRecipeAnomaly(detail.id, anomalyId);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mt-4 card p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>
          Source: <strong className="text-slate-700">{detail.source}</strong>
          {detail.generationModel && <> · {detail.generationModel}</>}
        </span>
        <span>
          Review:{' '}
          <strong className="text-slate-700">{detail.reviewStatus}</strong>
          {detail.lastReviewedBy && (
            <>
              {' '}by {detail.lastReviewedBy}
              {detail.lastReviewedAt && <> · {formatRelativeTime(detail.lastReviewedAt)}</>}
            </>
          )}
        </span>
        {resolvedCount > 0 && (
          <span>{resolvedCount} resolved finding(s) in history</span>
        )}
        {detail.reviewStatus !== 'human_reviewed' && (
          <button
            type="button"
            className="btn-primary text-xs ml-auto disabled:opacity-40"
            disabled={approving}
            onClick={() => void approve()}
            title="Mark this recipe human-reviewed and close all open findings. Does not change draft/verified — use the toggle for that."
          >
            {approving ? 'Approving…' : 'Approve ✓'}
          </button>
        )}
      </div>

      {open.length > 0 && (
        <ul className="space-y-2">
          {open.map((a) => (
            <li
              key={a.id}
              className="flex items-start justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm"
            >
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <span
                    className={
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ' +
                      SEVERITY_CLASSES[a.severity]
                    }
                  >
                    {a.severity}
                  </span>
                  <code className="text-xs text-slate-500">{a.kind}</code>
                  <span className="text-xs text-slate-400">{a.detectedBy}</span>
                </div>
                {a.message && <p className="text-slate-700">{a.message}</p>}
              </div>
              <button
                type="button"
                className="btn-outline text-xs shrink-0 disabled:opacity-40"
                disabled={busyId === a.id}
                onClick={() => void resolve(a.id)}
              >
                {busyId === a.id ? 'Resolving…' : 'Mark resolved'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-xs text-rose-700">{error}</p>}
    </div>
  );
}
